import { useCallback, useState } from 'react'
import { Box, Group, SegmentedControl, Stack } from '@mantine/core'
import { AppSwitch, AppTextarea } from '../../app/common/ui'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import { IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { CacheDetailDto, CacheVisibility } from '../../app/models/cache'
import { AppButton, AppInput, AppModal, AppText } from '../../app/common/ui'
import { CACHE_VISIBILITY_OPTIONS, CacheCover, resolveCacheCover, visibilityMeta } from './cacheUi'

interface Props {
  opened: boolean;
  cache: CacheDetailDto;
  onClose: () => void;
  onUpdated: (cache: CacheDetailDto) => void;
}

export default function CacheSettingsModal ({ opened, cache, onClose, onUpdated }: Props) {
  const [name, setName] = useState(cache.name)
  const [description, setDescription] = useState(cache.description ?? '')
  const [visibility, setVisibility] = useState<CacheVisibility>(cache.visibility ?? 'Public')
  // null = no uploaded cover (falls back to registry image when linked)
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(cache.coverImageUrl)
  const [isArchived, setIsArchived] = useState(cache.isArchived)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const registryFallback = cache.firearmModelCoverImageUrl?.trim() || null
  const effectiveCover = resolveCacheCover({ coverImageUrl, firearmModelCoverImageUrl: registryFallback })
  const usingRegistryFallback = !coverImageUrl && !!registryFallback

  const onDrop = useCallback(async (files: File[]) => {
    const file = files[0]
    if (!file) return
    setUploading(true)
    try {
      const result = await agent.Caches.uploadCover(file) as { url?: string } | undefined
      setCoverImageUrl(result?.url ?? null)
    } catch {
      toast.error('Failed to upload cover image')
    } finally {
      setUploading(false)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/gif,image/webp',
    maxFiles: 1,
    disabled: uploading
  })

  const save = async () => {
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    setSaving(true)
    try {
      const updated = await agent.Caches.update(cache.id, {
        name: name.trim(),
        description: description.trim(),
        // Send empty string to clear the uploaded cover (server reverts to registry fallback).
        coverImageUrl: coverImageUrl ?? '',
        visibility,
        isArchived
      })
      toast.success('Cache settings saved')
      onUpdated(updated)
      onClose()
    } catch {
      /* interceptor surfaces the error */
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppModal opened={opened} onClose={onClose} title="Cache settings" size="lg">
      <Stack spacing="lg">
        <AppInput
          label="Name"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.currentTarget.value)}
          maxLength={200}
          required
        />

        <AppTextarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={3}
          maxLength={2000}
          autosize
          maxRows={8}
        />

        {/* Cover image */}
        <Box>
          <AppText size="sm" weight={600} mb={6}>Cover image</AppText>
          <CacheCover url={effectiveCover} name={cache.name} seed={cache.id} aspectRatio="16 / 9" radius={10} loading={uploading} />
          {usingRegistryFallback && (
            <AppText size="xs" color="dimmed" mt={6}>
              Showing the linked registry model&apos;s image. Upload a cover to override it.
            </AppText>
          )}
          <Group spacing="xs" mt="sm">
            <Box
              {...getRootProps()}
              aria-label="Upload cover image"
              sx={{
                flex: 1,
                border: `1px dashed ${isDragActive ? 'var(--gs-accent)' : 'var(--gs-border)'}`,
                borderRadius: 8,
                padding: '10px 12px',
                cursor: uploading ? 'wait' : 'pointer',
                textAlign: 'center',
                '&:hover': { borderColor: 'var(--gs-accent)' }
              }}
            >
              <input {...getInputProps()} />
              <Group position="center" spacing={6} sx={{ color: 'var(--gs-muted)' }}>
                <IoCloudUploadOutline size={15} />
                <AppText size="xs" color="dimmed">
                  {uploading ? 'Uploading…' : isDragActive ? 'Drop image' : 'Upload / replace cover'}
                </AppText>
              </Group>
            </Box>
            {coverImageUrl && (
              <AppButton
                variant="light"
                color="red"
                size="sm"
                leftIcon={<IoTrashOutline size={14} />}
                onClick={() => setCoverImageUrl(null)}
              >
                {registryFallback ? 'Use registry image' : 'Remove'}
              </AppButton>
            )}
          </Group>
        </Box>

        {/* Visibility */}
        <Box>
          <AppText size="sm" weight={600} mb={6}>Visibility</AppText>
          <SegmentedControl
            fullWidth
            value={visibility}
            onChange={(v) => setVisibility(v as CacheVisibility)}
            data={CACHE_VISIBILITY_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
          />
          <AppText size="xs" color="dimmed" mt={6}>
            {visibilityMeta(visibility).description}
          </AppText>
        </Box>

        {/* Archive */}
        <Box
          sx={{
            border: '1px solid var(--gs-border)',
            borderRadius: 8,
            padding: '10px 12px',
            background: 'var(--gs-surface)'
          }}
        >
          <Group position="apart" align="flex-start" noWrap>
            <Box sx={{ flex: 1 }}>
              <AppText size="sm" weight={600}>Archive cache</AppText>
              <AppText size="xs" color="dimmed">
                Archived caches are hidden from everyone and removed from browse. You can restore later.
              </AppText>
            </Box>
            <AppSwitch
              checked={isArchived}
              onChange={(e) => setIsArchived(e.currentTarget.checked)}
              color="red"
            />
          </Group>
        </Box>

        <Group position="right" spacing="xs">
          <AppButton variant="default" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton color="ember" loading={saving} onClick={() => void save()}>Save changes</AppButton>
        </Group>
      </Stack>
    </AppModal>
  )
}
